import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  UserRound, Plus, Pencil, Trash2, Link2, Unlink, Copy, CheckCircle2, ShieldCheck,
} from 'lucide-react';

const emptyForm = { name: '', phone: '', is_dispatcher: false };

export default function DriversSection() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [bindLink, setBindLink] = useState(null);  // { driverName, link }

  const fetchAll = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/drivers`, { headers: getAuthHeaders() });
      setDrivers(res.data.drivers || []);
    } catch {
      toast.error('Не удалось загрузить водителей');
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openNew = () => { setForm(emptyForm); setEditing({}); };
  const openEdit = (d) => {
    setForm({ name: d.name || '', phone: d.phone || '', is_dispatcher: !!d.is_dispatcher });
    setEditing(d);
  };
  const close = () => { setEditing(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Введите имя водителя'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim() || null,
        is_dispatcher: form.is_dispatcher,
      };
      if (editing && editing.id) {
        const res = await axios.patch(`${API}/admin/drivers/${editing.id}`, payload, { headers: getAuthHeaders() });
        setDrivers(prev => prev.map(d => (d.id === editing.id ? res.data : d)));  // optimistic
        toast.success('Водитель обновлён');
      } else {
        const res = await axios.post(`${API}/admin/drivers`, payload, { headers: getAuthHeaders() });
        setDrivers(prev => [...prev, res.data]);  // optimistic
        toast.success('Водитель добавлен');
      }
      close();
      fetchAll(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Удалить водителя «${d.name}»?`)) return;
    setDrivers(prev => prev.filter(x => x.id !== d.id));  // optimistic
    try {
      await axios.delete(`${API}/admin/drivers/${d.id}`, { headers: getAuthHeaders() });
      toast.success('Удалено');
      fetchAll(false);
    } catch {
      toast.error('Ошибка удаления');
      fetchAll(false);  // restore on failure
    }
  };

  const genBindLink = async (d) => {
    try {
      const res = await axios.post(`${API}/admin/drivers/${d.id}/bind-link`, {}, { headers: getAuthHeaders() });
      setBindLink({ driverName: d.name, link: res.data.bind_link });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Не удалось создать ссылку');
    }
  };

  const unbind = async (d) => {
    if (!window.confirm(`Отвязать Telegram у «${d.name}»?`)) return;
    setDrivers(prev => prev.map(x => (x.id === d.id
      ? { ...x, is_bound: false, telegram_username: null, telegram_user_id: null } : x)));  // optimistic
    try {
      await axios.post(`${API}/admin/drivers/${d.id}/unbind`, {}, { headers: getAuthHeaders() });
      toast.success('Telegram отвязан');
      fetchAll(false);
    } catch {
      toast.error('Ошибка');
      fetchAll(false);
    }
  };

  const copyLink = () => {
    if (bindLink?.link) {
      navigator.clipboard.writeText(bindLink.link);
      toast.success('Ссылка скопирована');
    }
  };

  return (
    <Card data-testid="drivers-section" className="mt-4">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <UserRound className="w-4 h-4" /> Водители ({drivers.length})
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Привяжите водителя к Telegram, чтобы маршрут открывался у него лично в боте.
              Закрепите водителя за машиной в карточке машины выше.
            </p>
          </div>
          <Button onClick={openNew} size="sm" data-testid="add-driver-btn" className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-1" /> Добавить водителя
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-muted-foreground py-4">Загрузка...</div>
        ) : drivers.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            Водителей пока нет. Нажмите «Добавить водителя».
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {drivers.map(d => (
              <div
                key={d.id}
                data-testid={`driver-card-${d.id}`}
                className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow flex flex-col gap-2"
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-md ${d.is_dispatcher ? 'text-purple-600 bg-purple-50' : 'text-blue-600 bg-blue-50'}`}>
                    {d.is_dispatcher ? <ShieldCheck className="w-6 h-6" /> : <UserRound className="w-6 h-6" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{d.name}</div>
                    {d.phone && <div className="text-xs text-muted-foreground">{d.phone}</div>}
                  </div>
                  {d.is_dispatcher && <Badge className="bg-purple-600">Диспетчер</Badge>}
                </div>

                {d.is_bound ? (
                  <div className="text-xs flex items-center gap-1 text-emerald-700 bg-emerald-50 rounded px-2 py-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Привязан{d.telegram_username ? ` · @${d.telegram_username}` : ''}
                  </div>
                ) : (
                  <div className="text-xs flex items-center gap-1 text-amber-700 bg-amber-50 rounded px-2 py-1">
                    <Link2 className="w-3.5 h-3.5" /> Не привязан к Telegram
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-2 mt-auto">
                  {d.is_bound ? (
                    <Button variant="outline" size="sm" className="flex-1"
                      onClick={() => unbind(d)} data-testid={`unbind-driver-${d.id}`}>
                      <Unlink className="w-3 h-3 mr-1" /> Отвязать
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" className="flex-1 border-emerald-300 text-emerald-700"
                      onClick={() => genBindLink(d)} data-testid={`bind-driver-${d.id}`}>
                      <Link2 className="w-3 h-3 mr-1" /> Привязать
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => openEdit(d)} data-testid={`edit-driver-${d.id}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(d)}
                    data-testid={`delete-driver-${d.id}`}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Add / edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="driver-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Изменить водителя' : 'Новый водитель'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs text-muted-foreground">Имя *</label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="Имя водителя" data-testid="form-driver-fullname" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Телефон</label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                placeholder="+7..." data-testid="form-driver-phone-num" />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <div className="text-sm font-medium">Диспетчер</div>
                <div className="text-xs text-muted-foreground">Может открывать и пересобирать ЛЮБОЙ маршрут в своём боте</div>
              </div>
              <Switch checked={form.is_dispatcher}
                onCheckedChange={(v) => setForm({ ...form, is_dispatcher: v })}
                data-testid="form-driver-dispatcher" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} data-testid="driver-cancel">Отмена</Button>
            <Button onClick={save} disabled={saving} data-testid="driver-save" className="bg-emerald-600 hover:bg-emerald-700">
              {saving ? 'Сохраняю...' : 'Сохранить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bind-link dialog */}
      <Dialog open={!!bindLink} onOpenChange={(o) => !o && setBindLink(null)}>
        <DialogContent data-testid="bind-link-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ссылка-привязка для «{bindLink?.driverName}»</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Отправьте эту ссылку водителю. Он откроет её в Telegram и нажмёт «Start» —
              после этого бот сможет присылать ему маршрут лично.
            </p>
            <div className="flex items-center gap-2">
              <Input readOnly value={bindLink?.link || ''} data-testid="bind-link-value" className="text-xs" />
              <Button size="icon" variant="outline" onClick={copyLink} data-testid="bind-link-copy">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <a href={bindLink?.link} target="_blank" rel="noreferrer"
              className="text-xs text-emerald-700 underline">Открыть в Telegram</a>
          </div>
          <DialogFooter>
            <Button onClick={() => { setBindLink(null); fetchAll(false); }} data-testid="bind-link-done"
              className="bg-emerald-600 hover:bg-emerald-700">Готово</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
